import React, { useCallback, useEffect, useRef } from 'react';
import { Dropdown, Input } from 'antd';
import { MenuOutlined, SearchOutlined } from '@ant-design/icons';

import HomeIcon from '@assets/icons/homeIcon.svg?react';
import {
    AppHeader,
    BreadcrumbRow,
    CrumbCurrent,
    CrumbInactiveText,
    CrumbLink,
    CrumbSep,
    HeaderLeftCluster,
    HeaderRightCluster,
    HeaderSearchWrap,
    HeaderWorkspaceDropdown,
    SearchHotkeyHint,
} from '@/components/layout/insideShell.styles';
import { MobileDrawerButton, WorkspaceAvatar } from '@/layout/style';
import { SidebarIconSvg } from '@components/ui/SidebarIconSvg';
import { isMacOS } from '@utils/osDetection';
import NotificationHeaderDropdown from './NotificationHeaderDropdown';

export function InsideHeader({
    isMobile,
    onOpenMobileDrawer,
    breadcrumbItems,
    navigate,
    workspaceMenuItems,
    handleWorkspaceMenuClick,
    effectiveWorkspace,
}) {
    const workspaceLabel = effectiveWorkspace?.name || 'Workspace';
    const headerSearchRef = useRef(null);

    const focusHeaderSearch = useCallback(() => {
        const inst = headerSearchRef.current;
        const native = inst?.input ?? inst?.nativeElement ?? inst;
        if (native && typeof native.focus === 'function') {
            native.focus({ preventScroll: true });
            if (typeof native.select === 'function') native.select();
        }
    }, []);

    useEffect(() => {
        const onKeyDown = (e) => {
            if (e.key !== 'k' && e.key !== 'K') return;
            if (!(e.metaKey || e.ctrlKey)) return;
            if (e.altKey) return;
            const t = e.target;
            if (t instanceof HTMLElement) {
                const tag = t.tagName;
                if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
                if (t.isContentEditable) return;
            }
            e.preventDefault();
            focusHeaderSearch();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [focusHeaderSearch]);

    return (
        <AppHeader>
            <HeaderLeftCluster>
                <BreadcrumbRow aria-label="Breadcrumb">
                    {isMobile && (
                        <MobileDrawerButton onClick={onOpenMobileDrawer} style={{ marginRight: 8 }}>
                            <MenuOutlined />
                        </MobileDrawerButton>
                    )}
                    {breadcrumbItems.map((crumb, idx) => (
                        <React.Fragment key={crumb.key}>
                            {idx > 0 && <CrumbSep aria-hidden>{'>'}</CrumbSep>}
                            {idx === 0 ? (
                                <CrumbLink
                                    type="button"
                                    onClick={() => crumb.path && navigate(crumb.path)}
                                    aria-label="Home"
                                    style={{ width: 15, height: 15, padding: 0, alignItems: 'center', justifyContent: 'center' }}
                                >
                                    <SidebarIconSvg Icon={HomeIcon} size={15} className="sidebar-icon-svg" />
                                </CrumbLink>
                            ) : crumb.path ? (
                                <CrumbLink type="button" onClick={() => navigate(crumb.path)}>
                                    {crumb.label}
                                </CrumbLink>
                            ) : idx === breadcrumbItems.length - 1 ? (
                                <CrumbCurrent>{crumb.label}</CrumbCurrent>
                            ) : (
                                <CrumbInactiveText>{crumb.label}</CrumbInactiveText>
                            )}
                        </React.Fragment>
                    ))}
                </BreadcrumbRow>
            </HeaderLeftCluster>

            <HeaderRightCluster>
                <HeaderSearchWrap>
                    <Input
                        ref={headerSearchRef}
                        placeholder="Search"
                        allowClear
                        prefix={<SearchOutlined />}
                        suffix={<SearchHotkeyHint>{isMacOS() ? '⌘ K' : 'Ctrl K'}</SearchHotkeyHint>}
                        aria-label="Global search. Press Command K or Control K to focus."
                        onClick={focusHeaderSearch}
                    />
                </HeaderSearchWrap>

                <NotificationHeaderDropdown />

                <Dropdown
                    menu={{ items: workspaceMenuItems, onClick: handleWorkspaceMenuClick }}
                    trigger={['click']}
                    placement="bottomRight"
                    overlayStyle={{ minWidth: 260 }}
                >
                    <HeaderWorkspaceDropdown
                        style={{ flexShrink: 0 }}
                        role="button"
                        tabIndex={0}
                        title={workspaceLabel}
                        aria-label={workspaceLabel}
                    >
                        <WorkspaceAvatar>{(effectiveWorkspace?.name || 'W').charAt(0)}</WorkspaceAvatar>
                    </HeaderWorkspaceDropdown>
                </Dropdown>
            </HeaderRightCluster>
        </AppHeader>
    );
}

export default InsideHeader;
